import { useEffect, useState } from "react";
import { getCornerstone } from "../utils/dicomLoader";

/**
 * Whether the CT behind a segmentation labelmap has streamed every frame.
 * "unknown": the labelmap names no source volume, so we can't tell (ENG-186).
 */
export type SourceVolumeState = "loading" | "loaded" | "unknown";

type CachedVolume = { referencedVolumeId?: string; loadStatus?: { loaded?: boolean } };
type GetVolume = (volumeId: string) => CachedVolume | undefined;

export function sourceVolumeState(
  labelmapVolumeId: string | null,
  getVolume: GetVolume,
): SourceVolumeState {
  if (!labelmapVolumeId) return "loading";
  const sourceVolumeId = getVolume(labelmapVolumeId)?.referencedVolumeId;
  // Don't strand the reader on a gate that can never open; the caller warns.
  if (!sourceVolumeId) return "unknown";
  return getVolume(sourceVolumeId)?.loadStatus?.loaded === true ? "loaded" : "loading";
}

/**
 * The labelmap (geometry) exists long before the source pixels arrive, so
 * actions that read the images wait for this instead (ENG-186 step 2).
 * Read from the cache on every render; the load-completed event only re-renders.
 */
export function useSourceVolumeLoaded(labelmapVolumeId: string | null): SourceVolumeState {
  const [, setLoadEvents] = useState(0);
  const getVolume: GetVolume = (volumeId) =>
    getCornerstone().cache.getVolume(volumeId) as CachedVolume | undefined;
  const state = sourceVolumeState(labelmapVolumeId, getVolume);

  useEffect(() => {
    if (!labelmapVolumeId) return;
    const cornerstone = getCornerstone();
    const getCached = (volumeId: string) =>
      cornerstone.cache.getVolume(volumeId) as CachedVolume | undefined;
    const sourceVolumeId = getCached(labelmapVolumeId)?.referencedVolumeId;
    if (!sourceVolumeId) return;
    const eventName = cornerstone.Enums.Events.IMAGE_VOLUME_LOADING_COMPLETED;
    const onLoaded = (evt: Event) => {
      if ((evt as CustomEvent<{ volumeId?: string }>).detail?.volumeId !== sourceVolumeId) return;
      setLoadEvents((count) => count + 1);
    };
    cornerstone.eventTarget.addEventListener(eventName, onLoaded);
    // The volume may have finished between render and subscribe.
    if (getCached(sourceVolumeId)?.loadStatus?.loaded === true) setLoadEvents((count) => count + 1);
    return () => cornerstone.eventTarget.removeEventListener(eventName, onLoaded);
  }, [labelmapVolumeId]);

  return state;
}
